import GameSetup from '../Game/GameSetup';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import UpdateableResourceComponent from '../Utility/Component/Observable/UpdateableResourceComponent';
import IReplace from '../Utility/Component/Optional/IReplace';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import IObservable from '../Utility/Observable/IObservable';
import TypeStrings from '../Utility/TypeStrings';
import { ccCultureID, CultureID } from './CultureID';

/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

export class DateTimeConfiguration
{
    // Time
    public showTime: boolean;

    public hour: string;
    public minute: string;
    public second: string;
    public timeStyle: string;

    // Date
    public year: string;
    public month: string;
    public day: string;
    public dateStyle: string;

    constructor()
    {
        this.showTime = true;

        this.hour = "2-digit";
        this.minute = "2-digit";
        this.second = "2-digit";
        this.timeStyle = 'medium';

        this.year = 'numeric';
        this.month = 'numeric';
        this.day = 'numeric';
        this.dateStyle = 'short';
    }
}

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class LocalizationComponent extends StateDependencyComponent implements IObservable<CultureID>
{
    protected _isActive: boolean = true;

    /** State on which the localization should set itself up. */
    @property({ type: StateReference, visible: true })
    protected setupOnState: StateReference = new StateReference(StateID.PostInitializing, true);

    /** Default file name of main localization json file. */
    @property()
    protected defaultResource: string = 'LocalizedTextData';
    public get DefaultResource(): string { return this.defaultResource; }

    @property([cc.String])
    protected resourceNames: string[] = ['Paytable'];

    @property()
    public get PerformCheck(): boolean { return false; }
    public set PerformCheck(value: boolean) { this.performCheck(); }

    @property({ type: ccCultureID })
    protected checkCulture: CultureID = CultureID.en_us;

    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent<GameSetup>();

    /** Event dispatched when the current culture has changed. */
    public static readonly OnCultureChangedEventName: string = "Culture.Changed";

    /** @hidden */
    private _cultureID: CultureID = CultureID.en_us;
    /** Accessor for the current [[CultureID]]. */
    public get Value(): CultureID { return this.GetValue(); }
    public set Value(value: CultureID) { this.SetValue(value); }
    public GetValue(): CultureID { return this._cultureID; }
    public SetValue(value: CultureID): void
    {
        if (this._cultureID === value || !CultureID[value]) { return; }
        this._oldValue = this._cultureID;
        this._cultureID = value;
        this.emitCultureChange();
    }

    private _oldValue: CultureID = null;
    public get OldValue(): CultureID { return this._oldValue; }

    public get CultureKey(): string { return LocalizationComponent.GetCultureKey(this._cultureID); }
    public static GetCultureKey(cultureID: CultureID): string
    {
        let culture: string = CultureID[cultureID];
        if (!culture) { return ''; }
        let parts: string[] = culture.split('_');
        return `${parts[0]}-${parts[1].toUpperCase()}`;
    }

    /** @hidden */
    private _currency: string = 'USD';
    /** Accessor for the current currency type. */
    public get Currency(): string { return this._currency; }
    /** Mutator forthe current curreny type. */
    public set Currency(value) { this._currency = value; }

    public get NumberFormatOptions(): Intl.NumberFormatOptions
    {
        return {
            style: "currency",
            currency: this._currency
        };
    }

    /** @hidden */
    private _resourceHash: any = null;
    /** @hidden */
    private _hasLoaded: boolean = false;
    public get HasLoaded(): boolean { return this._hasLoaded; }

    private _replacementResourceIds: Map<string, string> = new Map();
    private _hasReplacements: boolean = false;

    /** @hidden */
    protected setContexts(): void
    {
        this._gameSetupRef.SetContext(GameSetup);
    }
    private _dateStyleSupported: boolean = true;
    private _timeStyleSupported: boolean = true;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        try
        {
            (new Date()).toLocaleDateString(undefined, { dateStyle: 'short' } as any);
        }
        catch
        {
            this._dateStyleSupported = false;
        }

        try
        {
            (new Date()).toLocaleTimeString(undefined, { timeStyle: 'short' } as any);
        }
        catch
        {
            this._timeStyleSupported = false;
        }
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this.setupOnState);
        this.trySetCulture(this._cultureID);
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this.setupOnState.Value)
        {
            const completeState: () => any = () =>
            {
                this.completeState(event.State);
                this.emitCultureChange();
            };

            this._currency = this._gameSetupRef.Value.CurrencyCode;
            let cultureID: CultureID = this._gameSetupRef.Value.Locale;
            this.trySetCulture(cultureID || this._cultureID)
                .then(completeState).catch(completeState);
        }
    }

    /**
     * Sets the current [[CultureID]] to be used for localization.
     * @param cultureID The [[CultureID]] to set.
     */
    public SetCulture(cultureID: CultureID): Promise<Map<string, any>>
    {
        return this.trySetCulture(cultureID);
    }

    @Decorators.BindThis()
    private trySetCulture(cultureID: CultureID): Promise<any>
    {
        this._resourceHash = {};
        return new Promise<any>((resolve, reject) =>
        {
            let idToLoad: CultureID;
            switch (cultureID)
            {
                case CultureID.en_ca:
                case CultureID.en_cb:
                case CultureID.en_gb:
                case CultureID.en_ie:
                    idToLoad = CultureID.en_us;
                default:
                    idToLoad = cultureID;
            }

            this.loadResources(idToLoad)
                .then((datas: [string, any][]) =>
                {
                    for (let data of datas)
                    {
                        this._resourceHash[data[0]] = data[1];
                    }

                    this._hasLoaded = true;
                    this.SetValue(cultureID);
                    resolve(this._resourceHash);
                })
                .catch(() =>
                {
                    CLog.error(`Could not load culture: [${CultureID[cultureID]}].`);
                    reject(null);
                });
        });
    }

    /**
     * Creates a localized string from the provided resource key, inserting the supplied args into any template values.
     * @param key The resource key to fetch for the current localization.
     * @returns The localized string.
     */
    public GetStringResource(key: string, ...args: string[]): string
    {
        if (!key || !this._hasLoaded) { return ''; }
        if (this._hasReplacements) { key = this._replacementResourceIds.get(key) || key; }

        const keyParts: string[] = key.split(':');
        let fileName: string = keyParts[0];

        if (fileName !== key)
        {
            key = keyParts[1];
        }
        else
        {
            fileName = this.defaultResource;
        }

        const fileHash: any = this._resourceHash[fileName];
        if (!fileHash)
        {
            CLog.error(`Could not find resource dictionary for file: [${fileName}].`);
            return '';
        }

        const path: string[] = key.split('/');

        let count: number = 0;
        let current: any = fileHash[path[count++]];

        while (current)
        {
            // Reached end of path, this is the string we want.
            if (count > path.length - 1)
            {
                if (typeof(current) !== TypeStrings.string) { break; }

                args.forEach((value, index) => current = current.split(`{${index}}`).join(value));
                return current as string;
            }

            // Continue down path.
            current = current[path[count++]];
        }

        CLog.error(`Could not find resource with key: [${key}] for culture: [${CultureID[this._cultureID]}]`,
            null, LogTypes.Localization);

        return '';
    }

    public AddReplacementResourceId(replacements: IReplace<string>[]): void
    {
        replacements.forEach(replacement => {
            this._replacementResourceIds.set(replacement.OldValue, replacement.NewValue);
        }, this);
        this._hasReplacements = true;
        this.emitCultureChange();
    }

    // #region IObservable

    public HasValue(): boolean
    {
        return this._cultureID !== null && this._cultureID !== undefined;
    }

    public OnChanged(fnOnChange: (...args: any[]) => void): void
    {
        this.node.on(LocalizationComponent.OnCultureChangedEventName, fnOnChange);
    }

    public OffChanged(fnOnChange: (...args: any[]) => void): void
    {
        this.node.off(LocalizationComponent.OnCultureChangedEventName, fnOnChange);
    }

    // #endregion

    private loadResources(cultureID: CultureID): Promise<[string, any][]>
    {
        let culturKey: string = LocalizationComponent.GetCultureKey(cultureID);

        let promises: Promise<[string, any]>[] =
        [
            this.loadCultureResource(culturKey)
        ];

        for (let resource of this.resourceNames)
        {
            promises.push(
                this.loadCultureResource(culturKey, resource)
            );
        }

        return Promise.all(promises);
    }

    /** @hidden */
    private loadCultureResource(cultureKey: string, fileName: string = this.defaultResource): Promise<[string, any]>
    {
        let originalFileName: string = fileName;
        if (fileName !== this.defaultResource) { fileName = `extra/${fileName}`; }
        let url: string = cc.url.raw(`resources/i18n/${cultureKey}/${fileName}.json`);

        return new Promise<any>((resolve, reject) =>
        {
            cc.loader.load(url, (error: any, data: any) =>
            {
                data
                    ? resolve([originalFileName, data])
                    : reject(error);
            });
        });
    }

    private performCheck(): void
    {
        CLog.log(`Checking for resources for culture: [${this.CultureKey}].`, LogTypes.Editor, null);

        this.trySetCulture(this.checkCulture)
            .then(() =>
            {
                let scene: cc.Scene = cc.director.getScene();

                let updateableResources: UpdateableResourceComponent[]
                    = scene.getComponentsInChildren(UpdateableResourceComponent);

                for (let updateable of updateableResources)
                {
                    this.GetStringResource(updateable.LocalKey);
                }
            })
            .catch(() =>
            {
                CLog.error(`Could not load culture: [${this.checkCulture}].`);
            });
    }

    private emitCultureChange(): void
    {
        this.node.emit(LocalizationComponent.OnCultureChangedEventName);
    }

    public GetTranslatedDateTimeDisplay(
        timeSs: number,
        config: DateTimeConfiguration,
        serverTimeZoneOffset: number = 0): string
    {
        const serverTime: number = timeSs + serverTimeZoneOffset;
        const serverDate: Date = new Date(serverTime);

        const dateConfig: any = this._dateStyleSupported ? { dateStyle: config.dateStyle } :
        {
            day : config.day,
            month : config.month,
            year : config.year
        };

        const formattedDateString: string = serverDate.toLocaleDateString(this.CultureKey, dateConfig);
        let formattedTimeString: string = '';

        if (config.showTime)
        {
            const timeConfig: any = this._timeStyleSupported ? { timeStyle: config.timeStyle } :
            {
                hour : config.hour,
                minute : config.minute,
                second: config.second,
            };

            if (this._cultureID !== CultureID.fr_ca)
            {
                formattedTimeString = serverDate.toLocaleTimeString(this.CultureKey, timeConfig);
            }
            else {
                // Displays time in 24-Hour Clock with colons
                formattedTimeString = serverDate.toLocaleTimeString('en-GB', timeConfig);
            }
        }

        const formattedString: string = `${formattedDateString} ${formattedTimeString}`;
        return formattedString.split('UTC')[0] || formattedString;
    }
}
